
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { logout } from "../authClient";
import { Avatar, Box, Button, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";
import { Wordmark } from "./Logo";

const NAV = {
  text: "#EFE3CC",
  muted: "#ABA5C9",
  border: "rgba(239, 227, 204, 0.10)",
  panelSoft: "rgba(239, 227, 204, 0.04)",
  cyan: "#2BBDAF",
  red: "#F0686C",
};

export default function Navbar({ isLoggedIn, setIsLoggedIn, setUserId, displayName, compact = false }) {
  const navigate = useNavigate();

  // Rendered with no props at all from the marketing/dashboard scaffold (hero.jsx) —
  // just show the brand mark there instead of an empty account bar.
  const standalone = isLoggedIn === undefined;

  // Signs THIS device out on the server too (its session and refresh token),
  // not just in this tab. Never fails from the user's point of view.
  const handleLogout = async () => {
    await logout();
    setIsLoggedIn(false);
    setUserId(null);
    navigate("/login", { replace: true });
  };

  if (standalone) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <Wordmark on="dark" height={22} />
      </Stack>
    );
  }

  if (!isLoggedIn) return null;

  // Dashboard sidebar footer: who is signed in, with log out as a quiet icon
  // rather than a red pill competing with the broadcast controls.
  if (compact) {
    const initials = (displayName || "")
      .split(/\s+/).filter(Boolean).slice(0, 2)
      .map((w) => w[0].toUpperCase()).join("");
    return (
      <Stack direction="row" alignItems="center" spacing={1.25}>
        <Avatar
          component={Link} to="/settings/security" aria-label="My account"
          sx={{
            width: 38, height: 38, fontSize: 13, fontWeight: 700, textDecoration: "none",
            bgcolor: "rgba(43,189,175,0.14)", color: NAV.cyan, border: "1px solid rgba(43,189,175,0.4)"
          }}
        >
          {initials || <PersonIcon sx={{ fontSize: 18 }} />}
        </Avatar>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography noWrap sx={{ color: NAV.text, fontSize: 14, fontWeight: 600, textTransform: "capitalize" }}>
            {displayName || "My account"}
          </Typography>
          <Typography component={Link} to="/settings/security" sx={{
            color: NAV.muted, fontSize: 12, textDecoration: "none", "&:hover": { color: NAV.text }
          }}>
            View profile
          </Typography>
        </Box>
        <Tooltip title="Log out">
          <IconButton onClick={handleLogout} aria-label="Log out" sx={{
            color: NAV.muted, "&:hover": { color: NAV.red, bgcolor: "rgba(229,72,77,0.08)" }
          }}>
            <LogoutIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
    );
  }

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="flex-end"
      flexWrap="wrap"
      spacing={1.25}
      sx={{ pb: 1.5, borderBottom: `1px solid ${NAV.border}` }}
    >
      <Tooltip title="My account">
        <Button
          component={Link}
          to="/settings/security"
          startIcon={
            <Avatar sx={{ width: 22, height: 22, bgcolor: "rgba(43,189,175,0.16)", color: NAV.cyan }}>
              <PersonIcon sx={{ fontSize: 14 }} />
            </Avatar>
          }
          sx={{
            textTransform: "none", color: NAV.text, fontWeight: 700, fontSize: 13,
            bgcolor: NAV.panelSoft, border: `1px solid ${NAV.border}`, borderRadius: "999px",
            px: 1.5, py: 0.5, "&:hover": { bgcolor: "rgba(255,255,255,0.06)" }
          }}
        >
          {displayName || "My account"}
        </Button>
      </Tooltip>
      <Button
        onClick={handleLogout}
        startIcon={<LogoutIcon sx={{ fontSize: 16 }} />}
        sx={{
          textTransform: "none", color: NAV.red, fontWeight: 700, fontSize: 13,
          border: "1px solid rgba(229,72,77,0.35)", borderRadius: "999px",
          px: 1.5, py: 0.5, "&:hover": { bgcolor: "rgba(229,72,77,0.08)", borderColor: NAV.red }
        }}
      >
        Log out
      </Button>
    </Stack>
  );
}
